import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Edit, Trash2, DollarSign, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { api } from '@/services/api';

interface Expense {
  id: string;
  date: string | Date;
  category: string;
  description?: string;
  amount: number;
  paymentMethod?: 'cash' | 'card' | 'mobile';
  reference?: string;
  createdAt: string | Date;
  updatedAt: string | Date;
}

const ExpensesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    category: '',
    description: '',
    amount: '',
    paymentMethod: 'cash' as 'cash' | 'card' | 'mobile',
    reference: '',
  });

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      category: '',
      description: '',
      amount: '',
      paymentMethod: 'cash',
      reference: '',
    });
  };

  const { data: expenses = [], isLoading, isError } = useQuery<Expense[]>({
    queryKey: ['expenses'],
    queryFn: async () => {
      const res = await api.get('/api/expenses');
      return (res.data?.data?.expenses || res.data?.expenses || []) as Expense[];
    },
  });

  const filteredExpenses = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return expenses;
    return expenses.filter((e) => {
      return (
        (e.category || '').toLowerCase().includes(q) ||
        (e.description || '').toLowerCase().includes(q) ||
        (e.reference || '').toLowerCase().includes(q)
      );
    });
  }, [expenses, searchTerm]);

  const totalAmount = useMemo(() => {
    return filteredExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  }, [filteredExpenses]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-MW', {
      style: 'currency',
      currency: 'MWK',
    }).format(amount);
  };

  const formatDate = (dateValue: string | Date) => {
    return new Date(dateValue).toLocaleDateString('en-MW', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        date: formData.date,
        category: formData.category.trim(),
        description: formData.description.trim() || undefined,
        amount: parseFloat(formData.amount),
        paymentMethod: formData.paymentMethod,
        reference: formData.reference.trim() || undefined,
      };
      const res = await api.post('/api/expenses', payload);
      return res.data?.expense as Expense;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      setIsCreateModalOpen(false);
      resetForm();
      toast.success('Expense created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create expense');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editingExpense) return;
      const payload = {
        date: formData.date,
        category: formData.category.trim() || undefined,
        description: formData.description.trim() || undefined,
        amount: formData.amount ? parseFloat(formData.amount) : undefined,
        paymentMethod: formData.paymentMethod,
        reference: formData.reference.trim() || undefined,
      };
      const res = await api.put(`/api/expenses/${editingExpense.id}`, payload);
      return res.data?.expense as Expense;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      setIsEditModalOpen(false);
      setEditingExpense(null);
      resetForm();
      toast.success('Expense updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update expense');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/expenses/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      toast.success('Expense deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete expense');
    },
  });

  const openCreateModal = () => {
    resetForm();
    setEditingExpense(null);
    setIsCreateModalOpen(true);
    setIsEditModalOpen(false);
  };

  const openEditModal = (expense: Expense) => {
    setEditingExpense(expense);
    setFormData({
      date: new Date(expense.date).toISOString().split('T')[0],
      category: expense.category || '',
      description: expense.description || '',
      amount: String(expense.amount ?? ''),
      paymentMethod: (expense.paymentMethod || 'cash') as any,
      reference: expense.reference || '',
    });
    setIsEditModalOpen(true);
    setIsCreateModalOpen(false);
  };

  const closeModal = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setEditingExpense(null);
    resetForm();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Expenses</h1>
          <p className="mt-1 text-sm text-gray-600">Track and manage business expenses</p>
        </div>
        <Button onClick={openCreateModal}>
          <Plus className="h-4 w-4 mr-2" />
          Add Expense
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-100 rounded-lg p-3">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total (Filtered)</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalAmount)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-100 rounded-lg p-3">
                <DollarSign className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Count (Filtered)</p>
                <p className="text-2xl font-bold text-gray-900">{filteredExpenses.length}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <Input
              placeholder="Search category, description, reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon={<Search className="h-4 w-4 text-gray-400" />}
            />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">Expense Entries</h2>
          <div className="text-sm text-gray-600">Total: {filteredExpenses.length}</div>
        </div>

        <div className="card-body">
          {isLoading ? (
            <div className="text-sm text-gray-600">Loading expenses...</div>
          ) : isError ? (
            <div className="text-sm text-red-600">Failed to load expenses</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredExpenses.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-6 text-sm text-gray-500 text-center">
                        No expenses found
                      </td>
                    </tr>
                  ) : (
                    filteredExpenses.map((e) => (
                      <tr key={e.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatDate(e.date)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{e.category}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{e.description || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatCurrency(Number(e.amount) || 0)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{e.paymentMethod || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                          <div className="inline-flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openEditModal(e)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              loading={deleteMutation.isPending}
                              onClick={() => deleteMutation.mutate(e.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {(isCreateModalOpen || isEditModalOpen) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">{isCreateModalOpen ? 'Add Expense' : 'Edit Expense'}</h3>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <Input
                type="date"
                label="Date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              />
              <Input
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
              />
              <Input
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
              <Input
                label="Amount"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select
                  className="input"
                  value={formData.paymentMethod}
                  onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value as any })}
                >
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="mobile">Mobile</option>
                </select>
              </div>

              <Input
                label="Reference"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
              />
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={closeModal}>
                Cancel
              </Button>
              {isCreateModalOpen ? (
                <Button
                  onClick={() => createMutation.mutate()}
                  loading={createMutation.isPending}
                  disabled={!formData.category.trim() || !formData.amount}
                >
                  Save
                </Button>
              ) : (
                <Button
                  onClick={() => updateMutation.mutate()}
                  loading={updateMutation.isPending}
                  disabled={!formData.category.trim() || !formData.amount}
                >
                  Save
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesPage;
